import $http from '@/plugins/axios'
import $utils from '@/utils/utils'
import AppService from '@/services/app.service'
import {BaseService, BaseServiceLevel2} from '@/services/base.service'
import store from '@/store/store'
import $const from '@/utils/const'

const StatisticService = {
    getCallDurationByHour(campaignId, filter) {
        return $http.get(`/campaigns/${campaignId}/stat-call-duration-by-hour/`, {params: filter})
    },
    getCallTransferByHour(campaignId, filter) {
        return $http.get(`/campaigns/${campaignId}/stat-call-transfer-by-hour/`, {params: filter})
    },
    getCallConnectedByHour(campaignId, filter) {
        return $http.get(`/campaigns/${campaignId}/stat-call-connected-by-hour/`, {params: filter})
    }
}

const CampaignService = {
    StatisticService,
    BlacklistService: BaseServiceLevel2('campaigns', 'black-list'),
    VoiceSpeedService: BaseServiceLevel2('campaigns', 'voice-speed'),
    VoiceGroupService: BaseServiceLevel2('campaigns', 'voice-group'),

    CRUD: BaseService('campaigns'),

    getCampaigns(page_size, page, ordering, search, status = '', direction = '') {
        let params = {page_size, page, ordering, search, status, direction}
        let endPoint = $utils.buildQuery('/campaigns/', params)
        return $http.get(endPoint)
    },
    getCampaign(id) {
        return $http.get('/campaigns/' + id + '/')
    },
    createCampaign(model) {
        return $http.post('/campaigns/', model)
    },
    deleteCampaign(campaign) {
        return $http.delete('/campaigns/' + campaign.id + '/')
    },
    getUserPermissions(campaign) {
        return $http.get('/campaigns/' + campaign.id + '/users-assign/')
    },
    saveUserPermissions(campaign, model) {
        return $http.post('/campaigns/' + campaign.id + '/users-assign/', model)
    },
    deleteUserPermissions(campaign, userId) {
        return $http.delete('/campaigns/' + campaign.id + '/users-assign/' + userId + '/')
    },
    getChartCustomer(campaignID, from_date, to_date, size) {
        let params = {from_date, to_date, size}
        return $http.get($utils.buildQuery(`/campaigns/${campaignID}/stat-by-customer/`, params))
    },
    getCampaignsOverview(campaignID, from_date, to_date) {
        let params = {from_date, to_date}
        return Promise.all([
            $http.get($utils.buildQuery(`/campaigns/${campaignID}/stat-by-call/`, params)),
        ])
    },
    getPieChartConnectedCall(campaignID, from_date, to_date, filter) {
        let params = {from_date, to_date, filter}
        return Promise.all([
            $http.get($utils.buildQuery(`/campaigns/${campaignID}/stat-connected-call/`, params))
        ])
    },
    getCampaignsOutput(campaignId, filter) {
        let params = {
            page_size: filter.itemPerPage,
            page: filter.currentPage,
            from_date: filter.fromDate,
            from_time: filter.fromTime,
            to_date: filter.toDate,
            to_time: filter.toTime,
            status: filter.status,
            search: filter.phone
        }
        if (filter.ordering) {
            params['ordering'] = filter.ordering
        }
        if (filter.direction) {
            params['direction'] = filter.direction
        }
        let endPoint = $utils.buildQuery('/campaigns/' + campaignId + '/call-data/', params)
        return $http.get(endPoint)
    },
    sendCampaignReport(campaignId, from, to, includeNotAttempted) {
        let params = {
            from_date: from.date,
            from_time: from.time,
            to_date: to.date,
            to_time: to.time,
            include_not_attempted: includeNotAttempted,
        }
        let endPoint = $utils.buildQuery('/campaigns/' + campaignId + '/send-campaign-report/', params)
        return $http.get(endPoint)
    },
    exportData(campaignID, fromDate, toDate) {
        return $http.get('' +
            `/campaigns/${campaignID}/export-data-to-excel/?from_date=${fromDate}&to_date=${toDate}`,
            {responseType: 'blob'}
        )
    },
    getBots() {
        return $http.get('/bots/')
    },
    getNlus(tenantName) {
        return $http.get('/nlp-platform-bots/' + tenantName + '/')
    },
    getTemplates() {
        return $http.get('/scenario-templates/')
    },
    getCustomers(campaign, page_size, page, ordering, filter) {
        let params = {page_size, page, ordering, ...filter, search: filter.name}
        let endPoint = $utils.buildQuery('/campaigns/' + campaign.id + '/customers/', params)
        return $http.get(endPoint)
    },
    getCustomerStatus() {
        return $http.get('/customer-status/')
    },
    getCustomer(campaign, customer) {
        return $http.get(`/campaigns/${campaign.id}/customers/${customer.id}/`)
    },
    addCustomer(campaign, model) {
        return $http.post(`/campaigns/${campaign.id}/customers/`, model)
    },
    editCustomer(campaign, customer) {
        return $http.put(`/campaigns/${campaign.id}/customers/${customer.id}/`, customer)
    },
    removeCustomer(campaign, customer) {
        return $http.delete(`/campaigns/${campaign.id}/customers/${customer.id}/`)
    },
    deleteAllCustomer(campaignId) {
        return $http.delete(`/campaigns/${campaignId}/delete-all-customers/`)
    },
    deleteFromToCustomer(campaignId, filter) {
        return $http.delete(
            `/campaigns/${campaignId}/delete-from-to-customers/`,
            {params: filter}
        )
    },
    batchDeleteCustomer(campaignId, customerIdList) {
        return $http.post(`/campaigns/${campaignId}/batch-delete-customers/`, {
            customers: customerIdList
        })
    },
    getRepeatsData() {
        return $http.get('/campaign-schedule-repeats/')
    },
    getSchedules(campaignId) {
        return $http.get(`/campaigns/${campaignId}/schedules/`)
    },
    addSchedule(campaignId, model) {
        return $http.post(`/campaigns/${campaignId}/schedules/`, model)
    },
    updateSchedule(campaignId, model) {
        return $http.put(`/campaigns/${campaignId}/schedules/${model.id}/`, model)
    },
    removeSchedule(campaignId, id) {
        return $http.delete(`/campaigns/${campaignId}/schedules/${id}/`)
    },
    getConfigs(campaignId) {
        return $http.get(`/campaigns/${campaignId}/config/`)
    },
    saveConfig(campaign, model) {
        return $http.put(`/campaigns/${campaign.id}/config/`, model)
    },
    getVoices() {
        return $http.get(`/voices/`)
    },
    getServiceNumbers(campaign) {
        return $http.get(`/campaigns/${campaign.id}/service-number/`)
    },
    callNow(campaign, customer, model) {
        return $http.post(`/campaigns/${campaign.id}/customers/${customer.id}/call-out/`, model)
    },
    massCalling(campaign, model) {
        return $http.post(`/campaigns/${campaign.id}/call-now/`, model)
    },
    getCustomerTemplate(campaign) {
        return $http.get(`/campaigns/${campaign.id}/template-customers-file/`, {responseType: 'blob'})
    },
    getCustomerProperties(campaign) {
        return $http.get(`/campaigns/${campaign.id}/customer-properties/`)
    },
    saveCustomerProperty(campaign, model) {
        if (!model.campaign) {
            model.campaign = campaign.id
        }
        return $http.post(`/campaigns/${campaign.id}/customer-properties/`, model)
    },
    updateCustomerProperty(campaign, id, model) {
        return $http.put(`/campaigns/${campaign.id}/customer-properties/${id}/`, model)
    },
    removeCustomerProperty(campaign, id) {
        return $http.delete(`/campaigns/${campaign.id}/customer-properties/${id}/`)
    },
    getCampaignRoles() {
        return $http.get(`/campaign-roles/`)
    },
    getMailReportConfig(campaignId) {
        return $http.get(`/campaigns/${campaignId}/config-report-via-email/`)
    },
    saveMailReportConfig(campaignId, config) {
        return $http.post(`/campaigns/${campaignId}/config-report-via-email/`, config)
    },
    deleteMailReportConfig(campaignId, configId) {
        return $http.delete(`/campaigns/${campaignId}/config-report-via-email/${configId}/`)
    },
    updateMailReportConfig(campaignId, config) {
        return $http.put(`/campaigns/${campaignId}/config-report-via-email/${config.id}/`, config)
    },
    checkUploadCustomerDataFile(campaignId) {
        return $http.get(`/campaigns/${campaignId}/import-customers-async-status/`)
    },
    changeStatus(campaignId, status) {
        return $http.post(`/campaigns/${campaignId}/status/`, {status})
    },
    audit(campaignId, filter) {
        return $http.get(`/campaigns/${campaignId}/audit/`, {params: AppService.mapFilterPagination(filter)})
    },
    requestConsentZalo(config) {
        return $http.post(`/cronjobs/zalo-request-consent/`, config)
    },
    getDataMappingTemplate(campaign) {
        return $http.get(`/campaigns/${campaign.id}/template-data-mapping/`, {responseType: 'blob'})
    },
    getCampaignPermissionTemplate(campaign) {
        return $http.get(`/campaigns/${campaign.id}/template-campaign-permission/`, {responseType: 'blob'})
    },
    mappingVariableFromBot(campaign) {
        return $http.get(`/campaigns/${campaign.id}/mapping-variable-from-bot/`)
    },
    deleteAllBlacklist(campaignId) {
        return $http.delete(`/campaigns/${campaignId}/delete-all-black-list/`)
    },
    deleteSelectedBlacklist(campaignId, phoneList) {
        return $http.delete(`/campaigns/${campaignId}/delete-selected-black-list/`, {data: {'phone_list': phoneList}})
    },
    deleteBlackList(campaignId, id) {
        return $http.delete(`/campaigns/${campaignId}/black-list/${id}/`)
    },
    getBlackListTemplate(campaign) {
        return $http.get(`/campaigns/${campaign.id}/template-black-list/`, {responseType: 'blob'})
    },
    getCTConfig(campaignId) {
        return $http.get(`/campaigns/${campaignId}/treatment-config/`)
    },
    addCTConfig(campaignId, model) {
        return $http.post(`/campaigns/${campaignId}/treatment-config/`, model)
    },
    updateCTConfig(campaignId, data) {
        return $http.put(`/campaigns/${campaignId}/treatment-config/${data.id}/`, data)
    },
    deleteCTConfig(campaignId, id) {
        return $http.delete(`/campaigns/${campaignId}/treatment-config/${id}/`)
    },
    statCallsByCallDuration(campaignId, timeRange, columns) {
        return $http.get(`/campaigns/${campaignId}/stat-call-by-call-duration/`, {
            params: {
                ...timeRange,
                columns: columns ? columns : 10
            }
        })
    },
    statCallsByTime(campaignId, timeRange, interval) {
        return $http.get(`/campaigns/${campaignId}/stat-call-by-time/`, {
            params: {
                ...timeRange,
                interval: interval
            }
        })
    },
    checkConnectSuperset() {
        return $http.get('https://dap-explore.fpt.ai')
    },
    getSupersetDashboards(campaignId) {
        return $http.get(`/campaigns/${campaignId}/superset/dashboard/`)
    },
    getCallTestInboundConfig(campaignId) {
        return $http.get(`/campaigns/${campaignId}/call-test-inbound-config/`)
    },
    isOutboundCaller() {
        let state = store.state
        let isAdmin = state.user.profile.roles && state.user.profile.roles.includes('admin')
        if (isAdmin) {
            return true
        }
        if (!state.campaign) return false
        let permissions = state.campaign.campaign ? state.campaign.campaign.permissions : []
        return permissions.includes($const.campaign.permission.ADMIN) ||
            permissions.includes($const.campaign.permission.OWNER) ||
            permissions.includes($const.campaign.permission.OUTBOUND_CALLER)

    },
    isAdminOrOwner() {
        let state = store.state
        let isAdmin = state.user.profile.roles && state.user.profile.roles.includes('admin')
        if (isAdmin) {
            return true
        }
        if (!state.campaign) return false
        let permissions = state.campaign.campaign ? state.campaign.campaign.permissions : []
        return permissions.includes($const.campaign.permission.ADMIN) ||
            permissions.includes($const.campaign.permission.OWNER)

    },
    checkUserCampaign(campaignId, data) {
        return $http.post(`/campaigns/${campaignId}/check-user-campaign/`, data)
    },
    getCustomerPropertiesTemplate(campaign) {
        return $http.get(`/campaigns/${campaign.id}/template-customer-properties/`, {responseType: 'blob'})
    },
    updateRequiredName(campaignId, data) {
        return $http.post(`/campaigns/${campaignId}/update-required-name/`, data)
    },
    allCustomerStatus(customerId) {
        return $http.get(`/customers/${customerId}/status/`)
    },
    getHistoryCall(campaignId, callId) {
        return $http.get(`/campaigns/${campaignId}/history/${callId}/`)
    },
    sendEmailUploadResult(campaignId, data) {
        return $http.post(`/campaigns/${campaignId}/send-upload-result/`, data)
    },
    getAnalysisCall(campaignId, callId) {
        return $http.get(`/campaigns/${campaignId}/analysis/${callId}/`)
    },
    getDefaultScript() {
        return $http.get('/default-script-group')
    }
}
export default CampaignService
